"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useSession } from "next-auth/react";
import {
    AnimatePresence,
    motion,
    useInView,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform,
} from "motion/react";
import Logo from "@/components/ui/Logo/Logo";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import AcceleratorLeadModal from "../AcceleratorLeadModal/AcceleratorLeadModal";
import TrainingFactSheet from "../TrainingFactSheet/TrainingFactSheet";
import AcceleratorSectionNav from "../AcceleratorSectionNav/AcceleratorSectionNav";
import { trainingFactValues } from "@/lib/trainingFacts";

// La page de l'accélérateur Rocket, refaite le 29/09/2026 sur la trame de la
// page Python : direction artistique pastel, BeBot avec son ordinateur et la
// petite fusée, un seul accent orange. Elle parle aux indépendants qui veulent
// vivre de la création de sites et d'applications.
//
// Les tarifs ne s'affichent pas ici (ils passent par l'appel avec le
// formateur) ; seule la fiche d'informations obligatoires donne le prix.
//
// Le PDF est public/static/programmes/programme-formation-developpeur-web.pdf.
// Il se régénère depuis l'administration (accélérateur Rocket, onglet
// « Programme PDF ») : après toute modification du programme ou de la fiche,
// remplacer ce fichier.

const PROGRAMME_PDF = "/static/programmes/programme-formation-developpeur-web.pdf";
const IMG = (fichier) => `/images/programmes/rocket/${fichier}`;
const IMG_PYTHON = (fichier) => `/images/programmes/python/${fichier}`;
const EASE = [0.22, 1, 0.36, 1];

// Les 27 modules du programme (lib/programmes/rocket.js) : heures de théorie
// et de pratique, ou projet-passerelle de deux semaines.
const MODULES = [
    [1, 1], [2, 5], [2, 8], [2, 6], [15, 60], [4, 20], [2, 12], [14, 100], null,
    [4, 20], [7, 30], [13, 80], [3, 20], null, [4, 20], [21, 130], null, [13, 100],
    [4, 20], [5, 35], [3, 9], [3, 16], [2, 4], [3, 9], [1, 3], [1, 3], null,
];

// Les grandes étapes du parcours, par numéro de module.
const PARTS = [
    { key: 1, from: 1, to: 4 },
    { key: 2, from: 5, to: 9 },
    { key: 3, from: 10, to: 15 },
    { key: 4, from: 16, to: 20 },
    { key: 5, from: 21, to: 27 },
];
const PARTS_VISIBLE = 2;

// Les quatre projets-passerelles et le module qu'ils concluent.
const PROJECTS = [9, 14, 17, 27];

const CREATES = ["vitrine", "boutique", "appli", "mobile", "api", "saas"];
const CREATE_ICONS = {
    vitrine: "creer-vitrine",
    boutique: "creer-boutique",
    appli: "creer-appli",
    mobile: "creer-mobile-app",
    api: "creer-api",
    saas: "creer-saas",
};

const SUMMARY = [
    { key: "summary_hours", icon: "clock-circle-linear" },
    { key: "summary_projects", icon: "code-square-bold-duotone" },
    { key: "summary_quiz", icon: "checklist-minimalistic-bold-duotone" },
    { key: "summary_follow", icon: "videocamera-record-bold-duotone" },
    { key: "summary_certification", icon: "diploma-verified-bold-duotone" },
    { key: "summary_access", icon: "infinite-bold" },
];

const FAQ = [
    "level",
    "time",
    "job",
    "schedule",
    "stuck",
    "freelance",
    "employment",
    "ai",
    "computer",
    "pro",
    "refund",
    "abroad",
    "funding",
];

const TIMELINE = [
    { phase: "apply", steps: ["apply", "call", "contract", "positioning"] },
    { phase: "training", steps: ["start", "weeks"] },
    { phase: "certification", steps: ["mock", "check", "exam"] },
    { phase: "after", steps: ["after"] },
];
const TIMELINE_ICONS = {
    apply: "document-text-bold-duotone",
    call: "videocamera-record-bold-duotone",
    contract: "shield-check-bold-duotone",
    positioning: "routing-2-bold-duotone",
    start: "calendar-bold-duotone",
    weeks: "code-square-bold-duotone",
    mock: "checklist-minimalistic-bold-duotone",
    check: "monitor-bold-duotone",
    exam: "diploma-verified-bold-duotone",
    after: "infinite-bold",
};
const TIMELINE_WEEKS = ["modules", "projects", "quiz", "follow"];
const RECAP = ["apply", "training", "certification", "after"];

const SPEAKERS = [
    ["lior-chamla", "Lior Chamla", "speaker_lior"],
    ["emmanuel-demey", "Emmanuel Demey", "speaker_emmanuel"],
    ["mansour-mahamat", "Mansour Mahamat", "speaker_mansour"],
    ["alexandre-soyer", "Alexandre Soyer", "speaker_alexandre"],
    ["axel-paris", "Axel Paris", "speaker_axel"],
];
const PERKS = [
    "jetbrains",
    "copilot",
    "namecheap",
    "azure",
    "tech",
    "heroku",
    "stripe",
    "mongodb",
    "1password",
];
// Les témoignages : Jérôme à l'écrit, avec sa grande photo ; Marie, Aurélien
// et Alliance en vidéo (Vimeo privé, lisible depuis believemy.com, les mêmes
// que sur l'ancienne page). Leurs photos ne font que 100 px : l'affiche de
// chaque vidéo est dessinée plutôt que tirée d'une capture.
const WRITTEN_TESTIMONIAL = { n: 1, photo: "jeromedelauney.jpg" };
const VIDEO_TESTIMONIALS = [
    { n: 2, photo: "mariecerutti.jpg", video: 845204936, tint: "from-[#E4DCFF] via-[#EEE8FF] to-[#D8ECFF]" },
    { n: 3, photo: "aureliengallea.jpg", video: 839648867, tint: "from-[#DDF6EA] via-[#EAF7F1] to-[#E6EEFF]" },
    { n: 4, photo: "alliance.jpeg", video: 842487119, tint: "from-[#FFE6D9] via-[#FFF1E8] to-[#F1ECFF]" },
];

const pad = (n) => String(n).padStart(2, "0");

function FadeIn({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.35, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

function Eyebrow({ children }) {
    return (
        <p className="mb-0 flex items-center gap-2.5 font-geistMono text-[11px] uppercase tracking-[0.18em] text-primary">
            <span className="h-1.5 w-1.5 bg-primary" />
            {children}
        </p>
    );
}

function SectionHead({ eyebrow, title, subtitle, className = "max-w-2xl" }) {
    return (
        <FadeIn className={className}>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                {title}
            </h2>
            {subtitle && (
                <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">{subtitle}</p>
            )}
        </FadeIn>
    );
}

function DownloadLink({ t, className = "" }) {
    return (
        <a
            href={PROGRAMME_PDF}
            target="_blank"
            rel="noopener"
            className={`group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:border-slate-400 hover:bg-slate-50 ${className}`}
        >
            <SolarIcon name="file-download-bold-duotone" className="text-lg text-primary" />
            {t("download")}
            <span className="hidden text-xs font-normal text-slate-500 sm:inline">{t("download_meta")}</span>
        </a>
    );
}

// Le hero : BeBot avec son ordinateur et la petite fusée, entouré de ce qu'un
// développeur manipule chaque jour. Chaque objet flotte à sa profondeur et
// suit la souris, plus ou moins selon sa distance.
const HERO_ITEMS = [
    { src: "navigateur", x: 0, y: 12, w: 23, depth: 1.4, rot: -8, float: 4.6 },
    { src: "palette", x: 76, y: 6, w: 19, depth: 1.1, rot: 8, float: 5.2 },
    { src: "code", x: -2, y: 56, w: 21, depth: 0.8, rot: -6, float: 5.8 },
    { src: "mobile", x: 82, y: 42, w: 14, depth: 1.6, rot: 8, float: 4.2 },
    { src: "base", x: 17, y: 83, w: 13, depth: 1.2, rot: -6, float: 5 },
    { src: "branches", x: 58, y: 0, w: 13, depth: 0.6, rot: 4, float: 6.4 },
    { src: "curseur", x: 22, y: -1, w: 12, depth: 1.8, rot: -10, float: 3.8 },
    { src: "fusee", x: 77, y: 78, w: 15, depth: 1, rot: 12, float: 5.6 },
];

const HERO_CHIPS = [
    { key: "hero_chip_online", x: -4, y: 38, depth: 1.3, delay: 0.5 },
    { key: "hero_chip_client", x: 42, y: 90, depth: 1.5, delay: 0.8 },
];

function useParallax(mx, my, depth, amplitude = 26) {
    const x = useSpring(useTransform(mx, (v) => v * depth * amplitude), {
        stiffness: 120,
        damping: 18,
        mass: 0.4,
    });
    const y = useSpring(useTransform(my, (v) => v * depth * amplitude), {
        stiffness: 120,
        damping: 18,
        mass: 0.4,
    });
    return { x, y };
}

function HeroItem({ item, mx, my, reduce, index }) {
    const { x, y } = useParallax(mx, my, item.depth);
    return (
        <motion.div
            aria-hidden
            className={`absolute ${index > 5 ? "hidden sm:block" : ""}`}
            style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.w}%`, x, y }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.25 + index * 0.05, ease: EASE }}
        >
            <motion.div
                className="cursor-grab"
                style={{ rotate: item.rot }}
                animate={reduce ? undefined : { y: [0, -10, 0] }}
                transition={{ duration: item.float, repeat: Infinity, ease: "easeInOut" }}
                whileHover={reduce ? undefined : { scale: 1.12, rotate: item.rot + 6 }}
            >
                <Image
                    src={IMG(`hero/${item.src}.webp`)}
                    alt=""
                    width={240}
                    height={240}
                    sizes="140px"
                    className="h-auto w-full select-none drop-shadow-[0_14px_18px_rgba(76,52,170,0.14)]"
                    draggable={false}
                />
            </motion.div>
        </motion.div>
    );
}

function HeroChip({ chip, t, mx, my, reduce }) {
    const { x, y } = useParallax(mx, my, chip.depth);
    return (
        <motion.div
            aria-hidden
            className="absolute hidden sm:block"
            style={{ left: `${chip.x}%`, top: `${chip.y}%`, x, y }}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, delay: chip.delay, ease: EASE }}
        >
            <motion.div
                animate={reduce ? undefined : { y: [0, -6, 0] }}
                transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: chip.delay }}
                className="flex items-center gap-2 whitespace-nowrap rounded-2xl bg-white/90 px-3.5 py-2.5 text-[13px] font-semibold text-slate-800 shadow-[0_12px_30px_-10px_rgba(76,52,170,0.35)] ring-1 ring-slate-900/5 backdrop-blur"
            >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                    <SolarIcon name="check-read-linear" className="text-sm text-emerald-600" />
                </span>
                {t(chip.key)}
            </motion.div>
        </motion.div>
    );
}

function HeroStage({ t, mx, my, reduce }) {
    const bebot = useParallax(mx, my, 0.45);
    const blob = useParallax(mx, my, -0.35);
    const tilt = useSpring(useTransform(mx, (v) => v * 6), { stiffness: 90, damping: 16 });
    return (
        <div className="relative mx-auto aspect-[1/1] w-full max-w-[540px]">
            <motion.div
                aria-hidden
                className="absolute inset-[9%] rounded-[42%_58%_46%_54%/52%_44%_56%_48%] bg-gradient-to-br from-[#DCE9FF] via-[#E6DEFF] to-[#FFE9DD]"
                style={{ x: blob.x, y: blob.y }}
                animate={reduce ? undefined : { rotate: [0, 6, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <div
                aria-hidden
                className="absolute left-[12%] top-[70%] h-[14%] w-[14%] rounded-full bg-[#FFE9B8]"
            />
            <div
                aria-hidden
                className="absolute right-[10%] top-[18%] h-[9%] w-[9%] rounded-full bg-[#DDF6EA]"
            />

            <motion.div
                className="absolute left-[21%] top-[11%] w-[58%]"
                style={{ x: bebot.x, y: bebot.y, rotate: tilt }}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: EASE }}
            >
                <motion.div
                    animate={reduce ? undefined : { y: [0, -12, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                    <Image
                        src={IMG("hero/bebot.webp")}
                        alt={t("hero_image_alt")}
                        width={900}
                        height={1115}
                        priority
                        sizes="(min-width: 1024px) 320px, 58vw"
                        className="h-auto w-full select-none"
                        draggable={false}
                    />
                </motion.div>
            </motion.div>

            {HERO_ITEMS.map((item, i) => (
                <HeroItem key={item.src} item={item} index={i} mx={mx} my={my} reduce={reduce} />
            ))}
            {HERO_CHIPS.map((chip) => (
                <HeroChip key={chip.key} chip={chip} t={t} mx={mx} my={my} reduce={reduce} />
            ))}
        </div>
    );
}

function Hero({ t, onApply }) {
    const facts = ["hours", "projects", "weeks", "certification"];
    const reduce = useReducedMotion();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);

    const onMove = (event) => {
        if (reduce || event.pointerType !== "mouse") return;
        const rect = event.currentTarget.getBoundingClientRect();
        mx.set((event.clientX - rect.left) / rect.width - 0.5);
        my.set((event.clientY - rect.top) / rect.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            className="relative overflow-hidden bg-[#F8F6FF]"
            onPointerMove={onMove}
            onPointerLeave={onLeave}
        >
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#E4DCFF] blur-3xl" />
                <div className="absolute -right-32 top-10 h-[480px] w-[480px] rounded-full bg-[#D8ECFF] blur-3xl" />
                <div className="absolute bottom-[-220px] left-1/3 h-[460px] w-[460px] rounded-full bg-[#FFE6D9] blur-3xl" />
                <div className="absolute right-1/4 top-2/3 h-[240px] w-[240px] rounded-full bg-[#FFF1C9] blur-3xl" />
            </div>

            <div className="relative mx-auto grid max-w-6xl items-center gap-6 px-6 pb-12 pt-10 sm:pt-14 lg:grid-cols-[1.02fr_1fr] lg:gap-4 lg:pb-16 lg:pt-16">
                <div className="order-2 lg:order-1">
                    <FadeIn>
                        <Eyebrow>{t("eyebrow")}</Eyebrow>
                    </FadeIn>
                    <FadeIn delay={0.04}>
                        <h1 className="mt-6 font-geistSans text-[2.6rem] font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-6xl">
                            {t("title_line1")}
                            <br />
                            <span className="text-primary">{t("title_line2")}</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.08}>
                        <p className="mb-0 mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
                            {t("subtitle")}
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.12}>
                        <div className="mt-9 flex flex-col items-stretch gap-2 sm:items-start">
                            <Button type="primary" onClick={onApply} className="w-full !rounded-full text-base font-semibold sm:w-auto">
                                <span className="inline-flex items-center gap-2">
                                    {t("cta_primary")}
                                    <SolarIcon name="arrow-right-linear" className="text-lg" />
                                </span>
                            </Button>
                            <span className="text-center text-xs text-slate-500 sm:text-left">{t("cta_benefit")}</span>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.16}>
                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                            <DownloadLink t={t} className="bg-white/70 backdrop-blur" />
                            <a
                                href="#programme"
                                className="inline-flex items-center gap-1.5 text-sm text-slate-500 underline-offset-4 transition-colors hover:text-slate-900 hover:underline"
                            >
                                {t("hero_programme_link")}
                                <SolarIcon name="arrow-right-linear" className="text-base" />
                            </a>
                        </div>
                    </FadeIn>
                </div>

                <div className="order-1 lg:order-2">
                    <HeroStage t={t} mx={mx} my={my} reduce={reduce} />
                </div>
            </div>

            <div className="relative mx-auto max-w-6xl px-6 pb-12 lg:pb-16">
                <div className="grid grid-cols-2 overflow-hidden rounded-3xl bg-white/75 ring-1 ring-slate-900/5 backdrop-blur sm:grid-cols-4">
                    {facts.map((fact, i) => (
                        <div
                            key={fact}
                            className={`px-6 py-6 sm:px-8 ${
                                i > 0 ? "sm:border-l sm:border-slate-900/5" : ""
                            } ${i % 2 === 1 ? "border-l border-slate-900/5" : ""} ${
                                i > 1 ? "border-t border-slate-900/5 sm:border-t-0" : ""
                            }`}
                        >
                            <p className="mb-0 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                                {t(`fact_${fact}_value`)}
                            </p>
                            <p className="mb-0 mt-1.5 text-sm text-slate-500">
                                {t(`fact_${fact}_label`)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// La réponse à « qu'est-ce que c'est ? », courte et citable telle quelle, puis
// trois repères illustrés (les icônes sont celles de la page Python).
const BRIEF = [
    { key: "who", icon: "icone-pour-qui.png" },
    { key: "how", icon: "icone-comment.png" },
    { key: "end", icon: "icone-a-la-fin.png" },
];

function Brief({ t }) {
    return (
        <section id="en-bref" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
                <FadeIn>
                    <div className="relative mx-auto aspect-[16/9] w-full max-w-xl overflow-hidden rounded-3xl">
                        <Image
                            src={IMG("en-bref.jpg")}
                            alt={t("brief_image_alt")}
                            fill
                            sizes="(min-width: 1024px) 540px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>

                <FadeIn delay={0.05}>
                    <Eyebrow>{t("brief_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.4rem] sm:leading-[1.1]">
                        {t("brief_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t.rich("brief_answer", {
                            strong: (chunks) => (
                                <span className="font-semibold text-slate-900">{chunks}</span>
                            ),
                        })}
                    </p>

                    <ul className="mb-0 mt-8 space-y-5 pl-0">
                        {BRIEF.map((item) => (
                            <li key={item.key} className="flex list-none items-start gap-4">
                                <span className="relative h-14 w-14 shrink-0 rounded-2xl bg-slate-50">
                                    <Image
                                        src={IMG_PYTHON(item.icon)}
                                        alt=""
                                        fill
                                        sizes="56px"
                                        className="object-contain p-1.5"
                                    />
                                </span>
                                <div>
                                    <h3 className="mb-0 text-base font-semibold text-slate-900">
                                        {t(`brief_${item.key}_title`)}
                                    </h3>
                                    <p className="mb-0 mt-1 text-[15px] leading-relaxed text-slate-600">
                                        {t(`brief_${item.key}_text`)}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <p className="mb-0 mt-8 flex items-center gap-2.5 text-sm text-slate-500">
                        <SolarIcon name="shield-check-bold-duotone" className="text-lg text-[#613BDB]" />
                        {t("brief_org")}
                    </p>
                </FadeIn>
            </div>
        </section>
    );
}

// Un extrait de ce que l'on écrit pour chaque réalisation. Les noms suivent
// la langue de la page.
const CREATE_CODE = {
    fr: {
        vitrine: [
            "<header class=\"accueil\">",
            "  <h1>Boulangerie Martin</h1>",
            "  <p>Pain au levain, cuit chaque matin</p>",
            "  <a href=\"#contact\">Commander</a>",
            "</header>",
        ],
        boutique: [
            "$produits = $bdd->query(\"SELECT * FROM produits\");",
            "",
            "foreach ($produits as $produit) {",
            "    echo \"<h2>{$produit['nom']}</h2>\";",
            "}",
        ],
        appli: [
            "const [creneaux, setCreneaux] = useState([]);",
            "",
            "return creneaux.map((creneau) => (",
            "  <Creneau key={creneau.id} {...creneau} />",
            "));",
        ],
        mobile: [
            "export default function Reservation({ cours }) {",
            "  return cours.map((seance) => (",
            "    <Seance key={seance.id} {...seance}",
            "      onPress={() => reserver(seance)} />",
            "  ));",
            "}",
        ],
        api: [
            "export async function GET() {",
            "  const commandes = await db.commande.findMany();",
            "  return Response.json(commandes);",
            "}",
        ],
        saas: [
            "export default async function Tableau() {",
            "  const session = await auth();",
            "  const projets = await getProjets(session.user.id);",
            "  return <ListeProjets projets={projets} />;",
            "}",
        ],
    },
    en: {
        vitrine: [
            "<header class=\"home\">",
            "  <h1>Martin's Bakery</h1>",
            "  <p>Sourdough bread, baked every morning</p>",
            "  <a href=\"#contact\">Order</a>",
            "</header>",
        ],
        boutique: [
            "$products = $db->query(\"SELECT * FROM products\");",
            "",
            "foreach ($products as $product) {",
            "    echo \"<h2>{$product['name']}</h2>\";",
            "}",
        ],
        appli: [
            "const [slots, setSlots] = useState([]);",
            "",
            "return slots.map((slot) => (",
            "  <Slot key={slot.id} {...slot} />",
            "));",
        ],
        mobile: [
            "export default function Booking({ classes }) {",
            "  return classes.map((session) => (",
            "    <Session key={session.id} {...session}",
            "      onPress={() => book(session)} />",
            "  ));",
            "}",
        ],
        api: [
            "export async function GET() {",
            "  const orders = await db.order.findMany();",
            "  return Response.json(orders);",
            "}",
        ],
        saas: [
            "export default async function Dashboard() {",
            "  const session = await auth();",
            "  const projects = await getProjects(session.user.id);",
            "  return <ProjectList projects={projects} />;",
            "}",
        ],
    },
};

// Coloration minimale : chaînes, balises et mots-clés, sans librairie.
function CodeLine({ line }) {
    if (!line) return <span>&nbsp;</span>;
    const parts = [];
    const regex =
        /("[^"]*"|'[^']*')|(<\/?[A-Za-z][\w.]*)|\b(import|from|export|default|async|await|function|const|return|foreach|as|echo)\b/g;
    let last = 0;
    let match;
    while ((match = regex.exec(line))) {
        if (match.index > last) parts.push(<span key={last}>{line.slice(last, match.index)}</span>);
        parts.push(
            <span
                key={match.index}
                className={
                    match[1] ? "text-[#F4A27F]" : match[2] ? "text-[#7BE0A8]" : "text-[#8FB4E8]"
                }
            >
                {match[0]}
            </span>,
        );
        last = match.index + match[0].length;
    }
    if (last < line.length) parts.push(<span key={last}>{line.slice(last)}</span>);
    return parts;
}

function Creates({ t }) {
    const locale = useLocale();
    const [active, setActive] = useState(0);
    const key = CREATES[active];
    const code = (CREATE_CODE[locale] || CREATE_CODE.fr)[key];

    return (
        <section className="border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead
                    eyebrow={t("creates_eyebrow")}
                    title={t("creates_title")}
                    subtitle={t("creates_subtitle")}
                />

                <div className="mt-14 grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-10">
                    <div
                        role="tablist"
                        aria-label={t("creates_eyebrow")}
                        className="-mx-6 flex scroll-px-6 gap-2 overflow-x-auto px-6 pb-4 pt-1 [scrollbar-width:none] lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0 lg:pt-0 [&::-webkit-scrollbar]:hidden"
                    >
                        {CREATES.map((item, i) => {
                            const selected = i === active;
                            return (
                                <button
                                    key={item}
                                    type="button"
                                    role="tab"
                                    id={`create-tab-${item}`}
                                    aria-selected={selected}
                                    aria-controls="create-panel"
                                    onClick={() => setActive(i)}
                                    className={`group flex shrink-0 cursor-pointer items-center gap-4 rounded-2xl border px-4 py-3 text-left text-slate-900 transition-colors lg:px-5 lg:py-3.5 ${
                                        selected
                                            ? "border-[#613BDB]/30 bg-[#F1ECFF] shadow-[0_10px_24px_-14px_rgba(97,59,219,0.5)]"
                                            : "border-slate-200 bg-white hover:border-[#613BDB]/20 hover:bg-[#FAF8FF]"
                                    }`}
                                >
                                    <span
                                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors ${
                                            selected ? "bg-white" : "bg-[#F6F3FF]"
                                        }`}
                                    >
                                        <Image
                                            src={IMG(`hero/${CREATE_ICONS[item]}.webp`)}
                                            alt=""
                                            width={160}
                                            height={160}
                                            sizes="36px"
                                            className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110"
                                        />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block whitespace-nowrap font-semibold lg:whitespace-normal">
                                            {t(`create_${item}_title`)}
                                        </span>
                                        <span
                                            className={`mt-0.5 hidden font-geistMono text-[11px] uppercase tracking-[0.12em] lg:block ${
                                                selected ? "text-[#613BDB]/70" : "text-slate-400"
                                            }`}
                                        >
                                            {t(`create_${item}_where`)}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div
                        id="create-panel"
                        role="tabpanel"
                        aria-labelledby={`create-tab-${key}`}
                        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#EAF1FF] via-[#F1ECFF] to-[#FFEFE6] text-slate-900 ring-1 ring-slate-900/5"
                    >
                        <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/50 blur-2xl" />
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div
                                key={key}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.22, ease: EASE }}
                                className="relative flex h-full flex-col p-6 sm:p-8"
                            >
                                <Image
                                    src={IMG(`hero/${CREATE_ICONS[key]}.webp`)}
                                    alt=""
                                    width={160}
                                    height={160}
                                    sizes="88px"
                                    className="absolute bottom-4 right-5 z-10 hidden h-16 w-16 rotate-6 object-contain drop-shadow-[0_12px_16px_rgba(76,52,170,0.22)] sm:block"
                                />
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-2xl bg-white/70 p-5 ring-1 ring-slate-900/5">
                                        <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                                            <SolarIcon name="user-rounded-bold-duotone" className="text-sm" />
                                            {t("creates_for")}
                                        </p>
                                        <p className="mb-0 mt-3 text-[15px] leading-relaxed text-slate-600">
                                            {t(`create_${key}_for`)}
                                        </p>
                                    </div>
                                    <div className="rounded-2xl bg-white p-5 ring-1 ring-primary/25 shadow-[0_12px_28px_-18px_rgba(232,81,29,0.55)]">
                                        <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-primary">
                                            <SolarIcon name="box-bold-duotone" className="text-sm" />
                                            {t("creates_you_build")}
                                        </p>
                                        <p className="mb-0 mt-3 text-[15px] leading-relaxed text-slate-900">
                                            {t(`create_${key}_build`)}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-4 flex-1 overflow-hidden rounded-2xl bg-[#1C1936] shadow-[0_18px_40px_-20px_rgba(28,25,54,0.6)]">
                                    <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-2.5">
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FF8A80]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FFD76E]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#7BE0A8]" />
                                        <span className="ml-3 font-geistMono text-xs text-white/40">
                                            {t(`create_${key}_file`)}
                                        </span>
                                    </div>
                                    <pre className="m-0 overflow-x-auto bg-transparent p-4 font-geistMono text-[13px] leading-6 text-white/85">
                                        <code>
                                            {code.map((line, i) => (
                                                <div key={i}>
                                                    <CodeLine line={line} />
                                                </div>
                                            ))}
                                        </code>
                                    </pre>
                                </div>

                                <p className="mb-0 mt-4 flex items-center gap-2 text-sm text-slate-500">
                                    <SolarIcon name="routing-2-bold-duotone" className="text-base text-[#613BDB]" />
                                    {t("creates_learned_in", { where: t(`create_${key}_where`) })}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}

// L'IA, sans la nier : la parole est au formateur, et la scène montre ce qui
// fait le métier (le client qui appelle un vendredi soir). Le futur
// accélérateur pour vibecoders (décembre 2026) est cité pour ceux qui veulent
// seulement lancer leur produit.
// La liste d'attente de l'accélérateur pour vibecoders (ouverture prévue en
// décembre 2026, nom à venir). Elle passe par la route des listes d'attente
// des formations, qui dédoublonne et prévient l'équipe par e-mail. Un membre
// s'inscrit d'un clic ; un visiteur ouvre un petit champ pour son adresse,
// dont son navigateur garde le souvenir.
const VIBE_SLUG = "accelerateur-vibecoders";
const VIBE_STORAGE = `believemy_waitlist_${VIBE_SLUG}`;

function VibeWaitlist({ t }) {
    const { data: session, status } = useSession();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle"); // idle | loading | joined | error

    useEffect(() => {
        if (status === "loading") return undefined;
        let cancelled = false;
        (async () => {
            if (!session?.user) {
                try {
                    if (window.localStorage.getItem(VIBE_STORAGE) && !cancelled) setState("joined");
                } catch (error) {
                    // Stockage indisponible : on propose simplement l'inscription.
                }
                return;
            }
            try {
                const response = await fetch("/api/course-waitlist/status", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ courseSlug: VIBE_SLUG }),
                });
                const data = await response.json();
                if (!cancelled && data?.joined) setState("joined");
            } catch (error) {
                // Une demande en double est sans effet : on laisse le bouton.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [session?.user, status]);

    const join = async (event) => {
        event?.preventDefault();
        if (state === "loading") return;
        if (!session?.user && !email.trim()) return;
        setState("loading");
        try {
            const response = await fetch("/api/course-waitlist/join", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    courseSlug: VIBE_SLUG,
                    courseName: "Accélérateur pour vibecoders (décembre 2026)",
                    email: session?.user ? null : email.trim(),
                }),
            });
            if (!response.ok) throw new Error("refus");
            try {
                window.localStorage.setItem(
                    VIBE_STORAGE,
                    JSON.stringify({ at: new Date().toISOString() }),
                );
            } catch (error) {
                // L'inscription est en base, seul le rappel visuel est perdu.
            }
            setState("joined");
        } catch (error) {
            setState("error");
        }
    };

    if (state === "joined") {
        return (
            <p className="mb-0 mt-4 flex items-start gap-2 text-sm text-slate-700">
                <SolarIcon name="check-circle-bold" className="mt-0.5 shrink-0 text-base text-primary" />
                {t("vibe_joined")}
            </p>
        );
    }

    return (
        <div className="mt-4">
            <AnimatePresence mode="wait" initial={false}>
                {!open ? (
                    <motion.button
                        key="button"
                        type="button"
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18, ease: EASE }}
                        onClick={() => (session?.user ? join() : setOpen(true))}
                        disabled={state === "loading"}
                        className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#613BDB]/25 bg-[#F6F3FF] px-4 py-2 text-sm font-semibold text-[#613BDB] transition-colors hover:border-[#613BDB]/40 hover:bg-[#EFE9FF] disabled:opacity-60"
                    >
                        <SolarIcon name="bell-bing-bold-duotone" className="text-base" />
                        {t("vibe_button")}
                    </motion.button>
                ) : (
                    <motion.form
                        key="form"
                        onSubmit={join}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.22, ease: EASE }}
                        className="max-w-md"
                    >
                        <label htmlFor="vibe-email" className="sr-only">
                            {t("vibe_email_label")}
                        </label>
                        <div className="flex flex-col gap-2 sm:flex-row sm:rounded-full sm:bg-white sm:p-1 sm:ring-1 sm:ring-slate-900/10">
                            <input
                                id="vibe-email"
                                type="email"
                                required
                                autoFocus
                                autoComplete="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                placeholder={t("vibe_placeholder")}
                                className="min-w-0 flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-[15px] text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-[#613BDB] sm:border-0 sm:bg-transparent"
                            />
                            <Button type="primary" small isLoading={state === "loading"} className="!rounded-full">
                                {t("vibe_submit")}
                            </Button>
                        </div>
                        <p className="mb-0 mt-2 text-xs text-slate-500">{t("vibe_privacy")}</p>
                    </motion.form>
                )}
            </AnimatePresence>
            {state === "error" && <p className="mb-0 mt-2 text-sm text-red-600">{t("vibe_error")}</p>}
        </div>
    );
}

function ModernDev({ t }) {
    const fr = useLocale() === "fr";
    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
                <FadeIn>
                    <Eyebrow>{t("ai_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("ai_title")}
                    </h2>
                    <p className="mb-0 mt-6 text-lg leading-relaxed text-slate-600">{t("ai_p1")}</p>
                    <p className="mb-0 mt-4 text-lg leading-relaxed text-slate-600">{t("ai_p2")}</p>
                    <p className="mb-0 mt-4 text-[15px] leading-relaxed text-slate-500">{t("ai_p3")}</p>
                    <VibeWaitlist t={t} />
                    <div className="mt-8 flex items-center gap-3">
                        <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full ring-2 ring-[#F1ECFF]">
                            <Image
                                src={IMG_PYTHON("formateur.jpg")}
                                alt=""
                                fill
                                sizes="44px"
                                className="object-cover"
                            />
                        </span>
                        <span className="font-geistSans text-sm font-semibold text-slate-900">
                            {t("ai_signature")}
                        </span>
                    </div>
                </FadeIn>

                <FadeIn delay={0.05}>
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#EAF1FF] via-[#F1ECFF] to-[#FFEFE6] p-6 ring-1 ring-slate-900/5 sm:p-8">
                        <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/50 blur-2xl" />
                        <p className="relative mb-0 text-center font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                            {t("ai_card_time")}
                        </p>
                        <div className="relative mt-4 max-w-[88%] rounded-2xl rounded-tl-md bg-white p-4 shadow-[0_12px_30px_-16px_rgba(76,52,170,0.45)] ring-1 ring-slate-900/5">
                            <p className="mb-0 text-xs font-semibold text-[#613BDB]">{t("ai_card_client")}</p>
                            <p className="mb-0 mt-1.5 text-[15px] leading-relaxed text-slate-800">
                                {t("ai_card_message")}
                            </p>
                        </div>
                        <div className="relative mt-4 overflow-hidden rounded-2xl bg-[#1C1936] shadow-[0_18px_40px_-20px_rgba(28,25,54,0.6)]">
                            <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-2.5">
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FF8A80]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FFD76E]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#7BE0A8]" />
                                <span className="ml-3 font-geistMono text-xs text-white/40">checkout.js</span>
                            </div>
                            <div className="space-y-1 p-4 font-geistMono text-[13px] leading-6">
                                <p className="mb-0 text-[#FF8A80]">✕ {t("ai_card_error")}</p>
                                <p className="mb-0 text-white/40">
                                    {fr ? "- const total = panier.total;" : "- const total = cart.total;"}
                                </p>
                                <p className="mb-0 text-[#7BE0A8]">
                                    {fr
                                        ? "+ const total = calculerTotal(panier);"
                                        : "+ const total = calculateTotal(cart);"}
                                </p>
                            </div>
                        </div>
                        <p className="relative mb-0 mt-4 ml-auto flex w-fit items-center gap-2 rounded-2xl rounded-tr-md bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_28px_-14px_rgba(232,81,29,0.7)]">
                            <SolarIcon name="check-circle-bold" className="text-base" />
                            {t("ai_card_fixed")}
                        </p>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function SummaryCard({ t, onApply }) {
    return (
        <div className="overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-28px_rgba(76,52,170,0.45)] ring-1 ring-slate-900/5">
            <div className="relative h-36 overflow-hidden bg-gradient-to-br from-[#DCE9FF] via-[#E9E2FF] to-[#FFE9DD]">
                <div aria-hidden className="absolute -left-6 bottom-[-30px] h-24 w-24 rounded-full bg-[#FFF1C9]" />
                <Image
                    src={IMG("hero/bebot.webp")}
                    alt=""
                    width={900}
                    height={1115}
                    sizes="120px"
                    className="absolute bottom-[-18px] left-1/2 h-[150px] w-auto -translate-x-1/2"
                />
                <Image
                    src={IMG("hero/navigateur.webp")}
                    alt=""
                    width={240}
                    height={216}
                    sizes="60px"
                    className="absolute right-5 top-4 h-auto w-14 rotate-6"
                />
                <Image
                    src={IMG("hero/curseur.webp")}
                    alt=""
                    width={225}
                    height={240}
                    sizes="60px"
                    className="absolute left-6 top-6 h-auto w-10 -rotate-12"
                />
            </div>
            <div className="p-6">
                <p className="mb-0 font-geistMono text-[11px] uppercase tracking-[0.18em] text-[#613BDB]">
                    {t("summary_eyebrow")}
                </p>
                <p className="mb-0 mt-3 font-geistSans text-2xl font-bold tracking-tight text-slate-900">
                    {t("summary_title")}
                </p>
                <ul className="mb-0 mt-6 space-y-3 border-t border-slate-100 pt-5">
                    {SUMMARY.map((row) => (
                        <li key={row.key} className="flex items-start gap-3 text-sm text-slate-700">
                            <SolarIcon name={row.icon} className="mt-0.5 shrink-0 text-base text-[#613BDB]" />
                            {t(row.key)}
                        </li>
                    ))}
                </ul>
                <Button type="primary" block onClick={onApply} className="mt-6 !rounded-full">
                    {t("cta_primary")}
                </Button>
                <a
                    href={PROGRAMME_PDF}
                    target="_blank"
                    rel="noopener"
                    className="mt-3 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:text-slate-900"
                >
                    <SolarIcon name="download-minimalistic-bold" className="text-base" />
                    {t("download")}
                </a>
            </div>
        </div>
    );
}

function ModuleItem({ t, n, isOpen, onToggle }) {
    const hours = MODULES[n - 1];
    const isProject = hours === null;
    return (
        <li className="list-none">
            <div
                className={`overflow-hidden rounded-2xl border bg-white transition-colors ${
                    isOpen ? "border-slate-300" : "border-slate-200"
                }`}
            >
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    className="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left sm:gap-5 sm:px-5"
                >
                    <div className="relative hidden h-14 w-24 shrink-0 overflow-hidden rounded-lg sm:block">
                        <Image
                            src={IMG(`module-${pad(n)}.jpg`)}
                            alt=""
                            fill
                            sizes="96px"
                            className="object-cover"
                        />
                    </div>
                    <span className="w-9 shrink-0 font-geistSans text-2xl font-bold text-primary">
                        {pad(n)}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-slate-900">
                            {t(`module_${n}_title`)}
                        </span>
                        <span className="mt-0.5 flex items-center gap-2 text-sm text-slate-500">
                            {isProject && (
                                <SolarIcon name="flag-bold-duotone" className="text-base text-primary" />
                            )}
                            {isProject
                                ? t("module_meta_project")
                                : t("module_meta", { theory: hours[0], practice: hours[1] })}
                        </span>
                    </span>
                    <SolarIcon
                        name="alt-arrow-down-linear"
                        className={`shrink-0 text-xl text-slate-400 transition-transform duration-300 ${
                            isOpen ? "rotate-180" : ""
                        }`}
                    />
                </button>
                <AnimatePresence initial={false}>
                    {isOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.28, ease: EASE }}
                        >
                            <div className="grid gap-5 border-t border-slate-100 px-5 pb-6 pt-5 sm:grid-cols-[1fr_220px]">
                                <div>
                                    <p className="mb-0 text-[15px] leading-relaxed text-slate-600">
                                        {t(`module_${n}_text`)}
                                    </p>
                                    <p className="mb-0 mt-4 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                                        {t("module_practice")}
                                    </p>
                                    <p className="mb-0 mt-1.5 text-[15px] font-medium text-slate-800">
                                        {t(`module_${n}_practice`)}
                                    </p>
                                </div>
                                <div className="relative aspect-[16/9] overflow-hidden rounded-xl sm:aspect-auto">
                                    <Image
                                        src={IMG(`module-${pad(n)}.jpg`)}
                                        alt=""
                                        fill
                                        sizes="220px"
                                        className="object-cover"
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </li>
    );
}

function Programme({ t, onApply }) {
    const [open, setOpen] = useState(1);
    const [showAll, setShowAll] = useState(false);
    const parts = showAll ? PARTS : PARTS.slice(0, PARTS_VISIBLE);
    const hidden = PARTS.slice(PARTS_VISIBLE).reduce((sum, p) => sum + p.to - p.from + 1, 0);

    return (
        <section id="programme" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead
                    eyebrow={t("programme_eyebrow")}
                    title={t("programme_title")}
                    subtitle={t("programme_subtitle")}
                />

                <div className="mt-14 grid items-start gap-10 lg:grid-cols-[1fr_360px]">
                    <div>
                        {parts.map((part) => (
                            <div key={part.key} className="mb-10 last:mb-0">
                                <p className="mb-4 flex items-center gap-3 font-geistMono text-[11px] uppercase tracking-[0.16em] text-[#613BDB]">
                                    <span className="rounded-full bg-[#F1ECFF] px-2.5 py-1 font-semibold">
                                        {pad(part.key)}
                                    </span>
                                    {t(`programme_part_${part.key}`)}
                                </p>
                                <ol className="mb-0 space-y-3 pl-0">
                                    {Array.from({ length: part.to - part.from + 1 }, (_, i) => {
                                        const n = part.from + i;
                                        return (
                                            <ModuleItem
                                                key={n}
                                                t={t}
                                                n={n}
                                                isOpen={open === n}
                                                onToggle={() => setOpen(open === n ? -1 : n)}
                                            />
                                        );
                                    })}
                                </ol>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => setShowAll(!showAll)}
                            aria-expanded={showAll}
                            className="mt-8 inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:border-slate-400 hover:bg-slate-50"
                        >
                            {showAll
                                ? t("programme_show_less")
                                : t("programme_show_all", { count: hidden })}
                            <SolarIcon
                                name="alt-arrow-down-linear"
                                className={`text-lg transition-transform duration-300 ${
                                    showAll ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                    </div>

                    <div className="lg:sticky lg:top-24">
                        <SummaryCard t={t} onApply={onApply} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Projects({ t }) {
    return (
        <section id="projets" className="scroll-mt-32 border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead
                    eyebrow={t("projects_eyebrow")}
                    title={t("projects_title")}
                    subtitle={t("projects_subtitle")}
                />

                <ol className="mb-0 mt-14 grid gap-5 pl-0 sm:grid-cols-2">
                    {PROJECTS.map((module, i) => (
                        <FadeIn key={module} delay={(i % 2) * 0.05}>
                            <li className="group h-full list-none overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-[0_18px_40px_-20px_rgba(11,16,32,0.35)]">
                                <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
                                    <Image
                                        src={IMG(`module-${pad(module)}.jpg`)}
                                        alt=""
                                        fill
                                        sizes="(min-width: 640px) 540px, 100vw"
                                        className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                                    />
                                    <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 font-geistSans text-sm font-bold text-slate-900 shadow-sm">
                                        #{i + 1}
                                    </span>
                                </div>
                                <div className="p-5 sm:p-6">
                                    <p className="mb-0 font-geistSans text-lg font-semibold text-slate-900">
                                        {t(`project_${i + 1}_title`)}
                                    </p>
                                    <p className="mb-0 mt-2 text-sm leading-relaxed text-slate-600">
                                        {t(`project_${i + 1}_text`)}
                                    </p>
                                    <p className="mb-0 mt-3 flex items-center gap-2 text-sm text-slate-500">
                                        <SolarIcon name="clock-circle-linear" className="text-base text-slate-400" />
                                        {t("projects_duration")}
                                        <span aria-hidden>·</span>
                                        {t("projects_after", { module: module - 1 })}
                                    </p>
                                </div>
                            </li>
                        </FadeIn>
                    ))}
                </ol>

                <FadeIn delay={0.1}>
                    <div className="relative mt-5 flex flex-col gap-6 overflow-hidden rounded-2xl bg-gradient-to-br from-[#E9E2FF] via-[#E6EEFF] to-[#FFEFE6] p-6 ring-1 ring-slate-900/5 sm:flex-row sm:items-center sm:p-8">
                        <Image
                            src={IMG("hero/code.webp")}
                            alt=""
                            width={240}
                            height={216}
                            sizes="80px"
                            className="h-auto w-16 shrink-0 -rotate-6 sm:w-20"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="mb-0 font-geistSans text-xl font-bold leading-snug text-slate-900">
                                {t("projects_more_title")}
                            </p>
                            <p className="mb-0 mt-2 text-sm leading-relaxed text-slate-600">
                                {t("projects_more_text")}
                            </p>
                        </div>
                        <DownloadLink t={t} className="shrink-0 self-start bg-white/80 sm:self-center" />
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function Rhythm({ t }) {
    const formulas = [
        { key: "full", image: "temps-plein.jpg" },
        { key: "part", image: "temps-partiel.jpg" },
    ];
    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead
                    eyebrow={t("rhythm_eyebrow")}
                    title={t("rhythm_title")}
                    subtitle={t("rhythm_subtitle")}
                />
                <div className="mt-14 grid gap-5 md:grid-cols-2">
                    {formulas.map((formula, i) => (
                        <FadeIn key={formula.key} delay={i * 0.05}>
                            <div className="h-full overflow-hidden rounded-3xl border border-slate-200 bg-white">
                                <div className="relative aspect-[16/9]">
                                    <Image
                                        src={IMG(formula.image)}
                                        alt={t(`rhythm_${formula.key}_alt`)}
                                        fill
                                        sizes="(min-width: 768px) 540px, 100vw"
                                        className="object-cover"
                                    />
                                </div>
                                <div className="p-6 sm:p-8">
                                    <p className="mb-0 font-geistMono text-[11px] uppercase tracking-[0.14em] text-[#613BDB]">
                                        {t(`rhythm_${formula.key}_title`)}
                                    </p>
                                    <p className="mb-0 mt-2 font-geistSans text-2xl font-bold tracking-tight text-slate-900">
                                        {t(`rhythm_${formula.key}_value`)}
                                    </p>
                                    <p className="mb-0 mt-3 text-[15px] leading-relaxed text-slate-600">
                                        {t(`rhythm_${formula.key}_text`)}
                                    </p>
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

function TimelineStep({ t, step, last }) {
    const ref = useRef(null);
    const reached = useInView(ref, { once: true, margin: "0px 0px -35% 0px" });
    const highlight = step === "exam";
    return (
        <li ref={ref} className={`relative list-none pl-14 ${last ? "" : "pb-10"}`}>
            <span
                className={`absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-500 ${
                    reached
                        ? "border-primary bg-primary text-white"
                        : "border-slate-200 bg-white text-slate-400"
                }`}
            >
                <SolarIcon name={TIMELINE_ICONS[step]} className="text-lg" />
            </span>
            <p className="mb-0 pt-0.5 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                {t(`timeline_${step}_when`)}
            </p>
            <div
                className={
                    highlight
                        ? "mt-2 rounded-2xl border border-primary/30 bg-primary/[0.04] p-5"
                        : "mt-1"
                }
            >
                <h3 className="mb-0 text-lg font-semibold text-slate-900">
                    {t(`timeline_${step}_title`)}
                </h3>
                {step === "weeks" ? (
                    <ul className="mb-0 mt-3 grid gap-2 pl-0 sm:grid-cols-2">
                        {TIMELINE_WEEKS.map((item) => (
                            <li
                                key={item}
                                className="flex list-none items-start gap-2 text-[15px] text-slate-600"
                            >
                                <SolarIcon
                                    name="check-circle-bold"
                                    className="mt-0.5 shrink-0 text-base text-primary"
                                />
                                {t(`timeline_weeks_${item}`)}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="mb-0 mt-1.5 text-[15px] leading-relaxed text-slate-600">
                        {t(`timeline_${step}_text`)}
                    </p>
                )}
            </div>
            {step === "exam" && (
                <div className="mt-3 flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                    <SolarIcon name="history-bold-duotone" className="mt-0.5 shrink-0 text-lg text-[#3D5A8A]" />
                    <p className="mb-0 text-sm leading-relaxed text-slate-600">
                        {t("timeline_exam_note")}
                    </p>
                </div>
            )}
        </li>
    );
}

function Timeline({ t }) {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 65%", "end 65%"],
    });
    const fill = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
    const steps = TIMELINE.flatMap((phase) => phase.steps);
    const lastStep = steps[steps.length - 1];

    return (
        <section className="border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-16">
                <div className="lg:sticky lg:top-28 lg:self-start">
                    <SectionHead
                        className=""
                        eyebrow={t("timeline_eyebrow")}
                        title={t("timeline_title")}
                        subtitle={t("timeline_subtitle")}
                    />
                    <div className="mt-10 grid grid-cols-2 gap-3">
                        {RECAP.map((item, i) => (
                            <div
                                key={item}
                                className={`rounded-2xl p-5 ${
                                    i === 1 ? "bg-[#F1ECFF] ring-1 ring-[#613BDB]/15" : "bg-white"
                                }`}
                            >
                                <p
                                    className={`mb-0 font-geistMono text-[11px] uppercase tracking-[0.14em] ${
                                        i === 1 ? "text-[#613BDB]" : "text-slate-400"
                                    }`}
                                >
                                    {t(`timeline_recap_${item}_label`)}
                                </p>
                                <p className="mb-0 mt-2 font-geistSans text-2xl font-bold tracking-tight text-slate-900">
                                    {t(`timeline_recap_${item}_value`)}
                                </p>
                                <p className="mb-0 mt-1 text-sm text-slate-500">
                                    {t(`timeline_recap_${item}_text`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div ref={ref} className="relative">
                    <span
                        aria-hidden
                        className="absolute bottom-5 left-5 top-5 w-px -translate-x-1/2 bg-slate-200"
                    />
                    <motion.span
                        aria-hidden
                        className="absolute bottom-5 left-5 top-5 w-[2px] origin-top -translate-x-1/2 bg-primary"
                        style={{ scaleY: reduce ? 1 : fill }}
                    />
                    {TIMELINE.map((phase) => (
                        <div key={phase.phase}>
                            <p className="relative mb-6 ml-14 inline-flex rounded-full bg-[#613BDB] px-3 py-1 text-xs font-semibold text-white">
                                {t(`timeline_phase_${phase.phase}`)}
                            </p>
                            <ol className="mb-0 pl-0">
                                {phase.steps.map((step) => (
                                    <TimelineStep key={step} t={t} step={step} last={step === lastStep} />
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FollowUp({ t }) {
    const points = ["weekly", "call", "questions", "corrections", "months"];
    return (
        <section id="suivi" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
                <FadeIn>
                    <div className="relative aspect-[1/1] overflow-hidden rounded-3xl">
                        <Image
                            src={IMG("formateur-suivi.jpg")}
                            alt={t("follow_image_alt")}
                            fill
                            sizes="(min-width: 1024px) 560px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>
                <FadeIn delay={0.05}>
                    <Eyebrow>{t("follow_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("follow_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("follow_subtitle")}
                    </p>
                    <ul className="mb-0 mt-8 space-y-4 pl-0">
                        {points.map((point) => (
                            <li key={point} className="flex list-none items-start gap-3">
                                <SolarIcon name="check-circle-bold" className="mt-0.5 shrink-0 text-xl text-primary" />
                                <span className="text-[15px] leading-relaxed text-slate-700">
                                    <span className="font-semibold text-slate-900">
                                        {t(`follow_${point}_title`)}
                                    </span>{" "}
                                    {t(`follow_${point}_text`)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </FadeIn>
            </div>
        </section>
    );
}

// Les ateliers en direct, la communauté, puis les outils offerts par
// GitHub Global Campus : ce que Rocket donne en plus des modules.
function Extras({ t }) {
    const figures = ["workshops", "replays", "access"];
    return (
        <section className="relative overflow-hidden bg-[#FFF8F1] px-6 py-20 sm:py-28">
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-[#FFE6D9] blur-3xl" />
                <div className="absolute -right-24 bottom-0 h-[380px] w-[380px] rounded-full bg-[#E4DCFF] blur-3xl" />
            </div>
            <div className="relative mx-auto max-w-6xl">
                <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <div>
                        <SectionHead
                            className=""
                            eyebrow={t("live_eyebrow")}
                            title={t("live_title")}
                            subtitle={t("live_subtitle")}
                        />
                        <FadeIn delay={0.05}>
                            <div className="mt-10 grid gap-5 border-t border-slate-900/10 pt-8 sm:grid-cols-3 sm:gap-0">
                                {figures.map((figure, i) => (
                                    <div
                                        key={figure}
                                        className={`flex items-baseline gap-4 sm:block ${i > 0 ? "border-t border-slate-900/10 pt-5 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0" : ""}`}
                                    >
                                        <p
                                            className={`mb-0 font-geistSans text-3xl font-bold tracking-tight sm:text-4xl ${
                                                i === 0 ? "text-primary" : "text-slate-900"
                                            }`}
                                        >
                                            {t(`live_${figure}_value`)}
                                        </p>
                                        <p className="mb-0 text-sm text-slate-500 sm:mt-1.5">
                                            {t(`live_${figure}_label`)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </FadeIn>
                    </div>
                    <FadeIn delay={0.05}>
                        <div className="relative aspect-[16/9] overflow-hidden rounded-3xl shadow-[0_30px_60px_-30px_rgba(76,52,170,0.4)]">
                            <Image
                                src={IMG("ateliers.jpg")}
                                alt={t("live_image_alt")}
                                fill
                                sizes="(min-width: 1024px) 560px, 100vw"
                                className="object-cover"
                            />
                        </div>
                        <div className="mt-4 flex items-start gap-4 rounded-2xl bg-white/80 p-5 ring-1 ring-slate-900/5">
                            <SolarIcon name="chat-round-dots-bold-duotone" className="mt-0.5 shrink-0 text-2xl text-[#613BDB]" />
                            <div>
                                <p className="mb-0 font-semibold text-slate-900">{t("live_community_title")}</p>
                                <p className="mb-0 mt-1 text-sm leading-relaxed text-slate-600">
                                    {t("live_community_text")}
                                </p>
                            </div>
                        </div>
                    </FadeIn>
                </div>

                <FadeIn delay={0.05}>
                    <p className="mb-0 mt-16 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                        {t("live_speakers_title")}
                    </p>
                    <ul className="mb-0 mt-5 grid grid-cols-2 gap-4 pl-0 sm:grid-cols-3 lg:grid-cols-5">
                        {SPEAKERS.map(([photo, name, role]) => (
                            <li key={photo} className="flex list-none items-center gap-3">
                                <span className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full ring-2 ring-white">
                                    <Image
                                        src={IMG(`${photo}.jpg`)}
                                        alt={name}
                                        fill
                                        sizes="48px"
                                        className="object-cover"
                                    />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold text-slate-900">{name}</span>
                                    <span className="block text-xs leading-snug text-slate-500">{t(role)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </FadeIn>

                <FadeIn delay={0.05}>
                    <div className="mt-16 rounded-3xl bg-white p-6 ring-1 ring-slate-900/5 sm:p-10">
                        <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-12">
                            <div>
                                <Eyebrow>{t("github_eyebrow")}</Eyebrow>
                                <h3 className="mb-0 mt-4 font-geistSans text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                                    {t("github_title")}
                                </h3>
                                <p className="mb-0 mt-4 text-[15px] leading-relaxed text-slate-600">
                                    {t("github_subtitle")}
                                </p>
                            </div>
                            <ul className="mb-0 grid gap-3 pl-0 sm:grid-cols-3">
                                {PERKS.map((perk) => (
                                    <li
                                        key={perk}
                                        className="flex list-none items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3"
                                    >
                                        <span className="relative h-8 w-8 shrink-0">
                                            <Image
                                                src={IMG(`logo-${perk}.png`)}
                                                alt=""
                                                fill
                                                sizes="32px"
                                                className="object-contain"
                                            />
                                        </span>
                                        <span className="text-sm leading-snug text-slate-700">
                                            {t(`perk_${perk}`)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function Trainer({ t }) {
    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
                <FadeIn className="lg:order-2">
                    <div className="relative mx-auto aspect-[4/5] max-w-md overflow-hidden rounded-3xl">
                        <Image
                            src={IMG_PYTHON("formateur.jpg")}
                            alt={t("trainer_name")}
                            fill
                            sizes="(min-width: 1024px) 460px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>
                <FadeIn delay={0.05} className="lg:order-1">
                    <Eyebrow>{t("trainer_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("trainer_name")}
                    </h2>
                    <p className="mb-0 mt-2 font-geistMono text-xs uppercase tracking-[0.16em] text-slate-500">
                        {t("trainer_role")}
                    </p>
                    <p className="mb-0 mt-6 text-lg leading-relaxed text-slate-600">
                        {t("trainer_text_1")}
                    </p>
                    <p className="mb-0 mt-4 text-lg leading-relaxed text-slate-600">
                        {t("trainer_text_2")}
                    </p>
                    <div className="mt-8 grid grid-cols-2 gap-3">
                        {["youtube", "since"].map((stat, i) => (
                            <div
                                key={stat}
                                className={`rounded-2xl p-5 ${
                                    i === 0 ? "bg-[#F1ECFF] ring-1 ring-[#613BDB]/15" : "bg-slate-50"
                                }`}
                            >
                                <p
                                    className={`mb-0 font-geistSans text-3xl font-bold tracking-tight ${
                                        i === 0 ? "text-primary" : "text-slate-900"
                                    }`}
                                >
                                    {t(`trainer_${stat}_value`)}
                                </p>
                                <p className="mb-0 mt-1 text-sm text-slate-500">
                                    {t(`trainer_${stat}_label`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function Certification({ t }) {
    const figures = ["questions", "minutes", "points"];
    return (
        <section id="certification" className="scroll-mt-32 relative overflow-hidden bg-[#F2FBF6] px-6 py-20 sm:py-28">
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-[#DDF6EA] blur-3xl" />
                <div className="absolute -right-24 bottom-0 h-[380px] w-[380px] rounded-full bg-[#E4DCFF] blur-3xl" />
            </div>
            <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
                <FadeIn>
                    <Eyebrow>{t("certification_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("certification_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-lg leading-relaxed text-slate-600">
                        {t("certification_text")}
                    </p>
                    <div className="mt-10 grid gap-5 border-t border-slate-900/10 pt-8 sm:grid-cols-3 sm:gap-0">
                        {figures.map((figure, i) => (
                            <div
                                key={figure}
                                className={`flex items-baseline gap-4 sm:block ${i > 0 ? "border-t border-slate-900/10 pt-5 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0" : ""}`}
                            >
                                <p
                                    className={`mb-0 font-geistSans text-3xl font-bold tracking-tight sm:text-4xl ${
                                        i === 2 ? "text-primary" : "text-slate-900"
                                    }`}
                                >
                                    {t(`certification_${figure}_value`)}
                                </p>
                                <p className="mb-0 text-sm text-slate-500 sm:mt-1.5">
                                    {t(`certification_${figure}_label`)}
                                </p>
                            </div>
                        ))}
                    </div>
                    <p className="mb-0 mt-8 text-sm leading-relaxed text-slate-500">
                        {t("certification_note")}
                    </p>
                </FadeIn>
                <FadeIn delay={0.05}>
                    <div className="relative aspect-[16/9] overflow-hidden rounded-3xl shadow-[0_30px_60px_-30px_rgba(76,52,170,0.4)]">
                        <Image
                            src={IMG("certification.jpg")}
                            alt={t("certification_image_alt")}
                            fill
                            sizes="(min-width: 1024px) 560px, 100vw"
                            className="object-cover"
                        />
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

// L'indicateur de satisfaction que Qualiopi demande de publier, en grand, puis
// les chiffres de notoriété et les témoignages, à part : ils ne mesurent pas
// la formation elle-même.
const MARKETING_STATS = ["youtube", "trustpilot", "members"];

function Stats({ t }) {
    const { rate } = trainingFactValues("rocket");
    const [video, setVideo] = useState(null);
    return (
        <section id="resultats" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead
                    eyebrow={t("stats_eyebrow")}
                    title={t("stats_title")}
                    subtitle={t("stats_subtitle")}
                />

                <FadeIn>
                    <div className="relative mt-12 overflow-hidden rounded-3xl bg-gradient-to-br from-[#E9E2FF] via-[#EEF2FF] to-[#FFF1E6] p-8 ring-1 ring-slate-900/5 sm:p-10">
                        <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/60 blur-2xl" />
                        <div className="relative">
                            <p className="mb-0 inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 font-geistMono text-[11px] uppercase tracking-[0.14em] text-[#613BDB]">
                                <SolarIcon name="shield-check-bold-duotone" className="text-sm" />
                                {t("stats_indicator_badge")}
                            </p>
                            <div className="mt-8 lg:flex lg:items-end lg:gap-12">
                                <p className="mb-0 shrink-0 font-geistSans text-7xl font-bold leading-none tracking-tight text-primary sm:text-8xl">
                                    {t("stats_rate_value", { rate })}
                                </p>
                                <div>
                                    <p className="mb-0 mt-4 font-geistSans text-2xl font-semibold text-slate-900 lg:mt-0">
                                        {t("stats_rate_label")}
                                    </p>
                                    <p className="mb-0 mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
                                        {t("stats_rate_method")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </FadeIn>

                <FadeIn delay={0.1}>
                    <div className="mt-10 grid gap-6 border-t border-slate-200 pt-8 sm:grid-cols-3">
                        {MARKETING_STATS.map((stat) => (
                            <div key={stat}>
                                <p className="mb-0 font-geistSans text-3xl font-bold tracking-tight text-slate-900">
                                    {t(`stats_${stat}_value`)}
                                </p>
                                <p className="mb-0 mt-1 text-sm text-slate-500">{t(`stats_${stat}_label`)}</p>
                            </div>
                        ))}
                    </div>
                </FadeIn>

                <FadeIn delay={0.1}>
                    <p className="mb-0 mt-20 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                        {t("testimonials_title")}
                    </p>
                </FadeIn>

                <FadeIn delay={0.1}>
                    <figure className="m-0 mt-5 grid overflow-hidden rounded-3xl bg-[#F8F6FF] ring-1 ring-slate-900/5 md:grid-cols-[280px_1fr]">
                        <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[300px]">
                            <Image
                                src={`/images/site/people/${WRITTEN_TESTIMONIAL.photo}`}
                                alt={t(`testimonial_${WRITTEN_TESTIMONIAL.n}_name`)}
                                fill
                                sizes="(min-width: 768px) 280px, 100vw"
                                className="object-cover object-top"
                            />
                        </div>
                        <div className="flex flex-col justify-center p-8 sm:p-10">
                            <div className="flex gap-0.5 text-[#F5B83D]" aria-hidden>
                                {Array.from({ length: 5 }, (_, s) => (
                                    <SolarIcon key={s} name="star-bold" className="text-lg" />
                                ))}
                            </div>
                            <blockquote className="m-0 mt-5 font-geistSans text-xl font-medium leading-snug tracking-tight text-slate-900 sm:text-2xl">
                                « {t(`testimonial_${WRITTEN_TESTIMONIAL.n}_quote`)} »
                            </blockquote>
                            <figcaption className="mt-6 text-sm">
                                <span className="font-semibold text-slate-900">
                                    {t(`testimonial_${WRITTEN_TESTIMONIAL.n}_name`)}
                                </span>
                                <span className="text-slate-500">
                                    {" · "}
                                    {t(`testimonial_${WRITTEN_TESTIMONIAL.n}_role`)}
                                </span>
                            </figcaption>
                        </div>
                    </figure>
                </FadeIn>

                <div className="mt-4 grid gap-4 md:grid-cols-3">
                    {VIDEO_TESTIMONIALS.map((item, i) => (
                        <FadeIn key={item.n} delay={i * 0.05}>
                            <figure className="m-0 flex h-full flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-slate-900/5">
                                <button
                                    type="button"
                                    onClick={() => setVideo(item)}
                                    aria-label={t("testimonial_video_title", {
                                        name: t(`testimonial_${item.n}_name`),
                                    })}
                                    className={`group relative flex aspect-[16/10] cursor-pointer items-center justify-center overflow-hidden bg-gradient-to-br ${item.tint}`}
                                >
                                    <span
                                        aria-hidden
                                        className="absolute -left-2 -top-8 font-geistSans text-[140px] font-bold leading-none text-white/70"
                                    >
                                        “
                                    </span>
                                    <span className="relative h-20 w-20 overflow-hidden rounded-full ring-4 ring-white shadow-[0_14px_30px_-12px_rgba(76,52,170,0.45)] transition-transform duration-300 group-hover:scale-105">
                                        <Image
                                            src={`/images/site/people/${item.photo}`}
                                            alt=""
                                            fill
                                            sizes="80px"
                                            className="object-cover"
                                        />
                                    </span>
                                    <span className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 py-1.5 pl-1.5 pr-3.5 text-sm font-semibold text-slate-900 shadow-sm backdrop-blur transition-colors group-hover:bg-white">
                                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white">
                                            <SolarIcon name="play-bold" className="text-xs" />
                                        </span>
                                        {t("testimonial_watch")}
                                    </span>
                                    <span className="absolute right-4 top-4 rounded-full bg-white/70 px-2.5 py-1 font-geistMono text-[10px] uppercase tracking-[0.12em] text-slate-600">
                                        {t("testimonials_video_hint")}
                                    </span>
                                </button>
                                <div className="flex flex-1 flex-col p-6">
                                    <blockquote className="m-0 flex-1 text-[15px] leading-relaxed text-slate-700">
                                        « {t(`testimonial_${item.n}_quote`)} »
                                    </blockquote>
                                    <figcaption className="mt-5">
                                        <span className="block text-sm font-semibold text-slate-900">
                                            {t(`testimonial_${item.n}_name`)}
                                        </span>
                                        <span className="block text-xs text-slate-500">
                                            {t(`testimonial_${item.n}_role`)}
                                        </span>
                                    </figcaption>
                                </div>
                            </figure>
                        </FadeIn>
                    ))}
                </div>
            </div>

            <BottomSheetModal
                isOpen={Boolean(video)}
                onClose={() => setVideo(null)}
                maxWidthClass="sm:max-w-3xl"
                panelClassName="dark:bg-bgDark-800"
            >
                {video && (
                    <div className="p-4 sm:p-6">
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <p className="mb-0 font-semibold text-slate-900 dark:text-white">
                                {t("testimonial_video_title", { name: t(`testimonial_${video.n}_name`) })}
                            </p>
                            <button
                                type="button"
                                onClick={() => setVideo(null)}
                                aria-label={t("testimonial_close")}
                                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                            >
                                <SolarIcon name="close-circle-linear" className="text-xl" />
                            </button>
                        </div>
                        <div className="relative aspect-[16/9] overflow-hidden rounded-2xl bg-slate-900">
                            <iframe
                                src={`https://player.vimeo.com/video/${video.video}?autoplay=1&title=0&byline=0&portrait=0`}
                                title={t("testimonial_video_title", { name: t(`testimonial_${video.n}_name`) })}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 h-full w-full"
                            />
                        </div>
                    </div>
                )}
            </BottomSheetModal>
        </section>
    );
}

// Après la formation, vu par un freelance : ce qu'il vendra et le tarif en
// dessous duquel il ne vit pas (chiffre de l'outil « Combien facturer »). Le
// salariat vient ensuite. La suite de parcours et les débouchés exigés par
// Qualiopi sont dans la fiche (trainingFacts_rocket_pathway), juste en dessous.
const CAREER_SELL = [1, 2, 3, 4, 5];
const CAREER_JOBS = [1, 2, 3, 4, 5];

function Career({ t }) {
    const locale = useLocale();
    return (
        <section className="border-t border-slate-200 bg-slate-50 px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead
                    eyebrow={t("career_eyebrow")}
                    title={t("career_title")}
                    subtitle={t("career_subtitle")}
                />
                <div className="mt-12 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
                    <FadeIn>
                        <div className="h-full rounded-3xl bg-white p-8 ring-1 ring-slate-900/5">
                            <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-[#613BDB]">
                                <SolarIcon name="case-round-bold-duotone" className="text-sm" />
                                {t("career_sell_title")}
                            </p>
                            <ul className="mb-0 mt-5 space-y-3.5 pl-0">
                                {CAREER_SELL.map((n) => (
                                    <li key={n} className="flex list-none items-start gap-3">
                                        <SolarIcon name="check-circle-bold" className="mt-0.5 shrink-0 text-lg text-primary" />
                                        <span className="text-[15px] leading-relaxed text-slate-800">
                                            {t(`career_sell_${n}`)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.05}>
                        <div className="relative h-full overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFF1E6] via-[#FFF8F1] to-[#F1ECFF] p-8 ring-1 ring-primary/20">
                            <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-primary">
                                <SolarIcon name="wallet-money-bold-duotone" className="text-sm" />
                                {t("career_rate_title")}
                            </p>
                            <p className="mb-0 mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                <span className="font-geistSans text-6xl font-bold tracking-tight text-slate-900">
                                    {t("career_rate_value")}
                                </span>
                                <span className="text-base font-medium text-slate-600">
                                    {t("career_rate_unit")}
                                </span>
                            </p>
                            <p className="mb-0 mt-4 text-[15px] leading-relaxed text-slate-600">
                                {t("career_rate_text")}
                            </p>
                            <Link
                                href={{
                                    pathname: "/tools/[slug]",
                                    params: { slug: locale === "fr" ? "combien-facturer" : "how-much-to-charge" },
                                }}
                                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 ring-1 ring-slate-900/10 transition-colors hover:ring-slate-900/25"
                            >
                                {t("career_rate_link")}
                                <SolarIcon name="arrow-right-linear" className="text-base" />
                            </Link>
                        </div>
                    </FadeIn>
                </div>
                <FadeIn delay={0.1}>
                    <div className="mt-4 rounded-3xl border border-slate-200 bg-white/70 p-8 sm:p-10">
                        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
                            <div>
                                <p className="mb-0 flex items-center gap-2 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-500">
                                    <SolarIcon name="buildings-2-bold-duotone" className="text-sm text-[#613BDB]" />
                                    {t("career_employee_title")}
                                </p>
                                <p className="mb-0 mt-4 text-lg leading-relaxed text-slate-700">
                                    {t("career_employee_text")}
                                </p>
                                <ul className="mb-0 mt-6 flex flex-wrap gap-2 pl-0">
                                    {CAREER_JOBS.map((n) => (
                                        <li
                                            key={n}
                                            className="list-none rounded-full bg-[#F1ECFF] px-3.5 py-1.5 text-sm font-medium text-[#4B2FB0]"
                                        >
                                            {t(`career_job_${n}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="lg:border-l lg:border-slate-200 lg:pl-14">
                                <div className="grid grid-cols-2 gap-6">
                                    {["junior", "senior"].map((level) => (
                                        <div key={level}>
                                            <p className="mb-0 font-geistSans text-3xl font-bold tracking-tight text-slate-900">
                                                {t(`career_salary_${level}_value`)}
                                            </p>
                                            <p className="mb-0 mt-1.5 text-sm leading-snug text-slate-500">
                                                {t(`career_salary_${level}_label`)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                                <p className="mb-0 mt-6 text-xs leading-relaxed text-slate-400">
                                    {t("career_salary_note")}
                                </p>
                            </div>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}

function Guarantees({ t }) {
    const items = [
        { key: "withdrawal", icon: "garantie-retractation" },
        { key: "firstwin", icon: "garantie-firstwin" },
    ];
    return (
        <section className="bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <SectionHead eyebrow={t("guarantees_eyebrow")} title={t("guarantees_title")} />
                <div className="mt-12 grid gap-4 md:grid-cols-2">
                    {items.map((item, i) => (
                        <FadeIn key={item.key} delay={i * 0.05}>
                            <div
                                className={`h-full rounded-3xl p-8 sm:p-10 ${
                                    i === 1
                                        ? "bg-gradient-to-br from-[#FFF1E6] via-[#FFF8F1] to-[#F1ECFF] ring-1 ring-primary/20"
                                        : "bg-slate-50 ring-1 ring-slate-900/5"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
                                        <Image
                                            src={IMG(`hero/${item.icon}.webp`)}
                                            alt=""
                                            width={160}
                                            height={160}
                                            sizes="48px"
                                            className="h-11 w-11 object-contain"
                                        />
                                    </span>
                                    <span className="rounded-full bg-white px-3 py-1 font-geistMono text-[11px] uppercase tracking-[0.14em] text-slate-500">
                                        {t(`guarantee_${item.key}_badge`)}
                                    </span>
                                </div>
                                <h3 className="mb-0 mt-6 font-geistSans text-2xl font-bold tracking-tight text-slate-900">
                                    {t(`guarantee_${item.key}_title`)}
                                </h3>
                                <p className="mb-0 mt-3 text-[15px] leading-relaxed text-slate-600">
                                    {t(`guarantee_${item.key}_text`)}
                                </p>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Faq({ t }) {
    const [open, setOpen] = useState(-1);
    return (
        <section id="faq" className="scroll-mt-32 bg-white px-6 py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1fr_1.6fr]">
                <FadeIn>
                    <Eyebrow>{t("faq_eyebrow")}</Eyebrow>
                    <h2 className="mt-4 font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.6rem] sm:leading-[1.1]">
                        {t("faq_title")}
                    </h2>
                    <p className="mb-0 mt-5 text-[15px] leading-relaxed text-slate-500">
                        {t("faq_contact")}
                    </p>
                </FadeIn>
                <div className="divide-y divide-slate-200 border-y border-slate-200">
                    {FAQ.map((question, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={question}>
                                <button
                                    type="button"
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    aria-expanded={isOpen}
                                    className="flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left"
                                >
                                    <span className="font-semibold text-slate-900">
                                        {t(`faq_${question}_q`)}
                                    </span>
                                    <SolarIcon
                                        name="alt-arrow-down-linear"
                                        className={`shrink-0 text-xl text-slate-400 transition-transform duration-300 ${
                                            isOpen ? "rotate-180" : ""
                                        }`}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25, ease: EASE }}
                                            className="overflow-hidden"
                                        >
                                            <p className="mb-0 pb-5 pr-10 text-[15px] leading-relaxed text-slate-600">
                                                {t(`faq_${question}_a`)}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// Le dernier panneau : BeBot décolle sur la fusée (la scène du dernier
// projet-passerelle).
function FinalCta({ t, onApply }) {
    return (
        <section className="bg-white px-6 pb-20 pt-4 sm:pb-28">
            <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-[#F8F6FF] ring-1 ring-slate-900/5">
                <div aria-hidden className="pointer-events-none absolute inset-0">
                    <div className="absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full bg-[#E4DCFF] blur-3xl" />
                    <div className="absolute -right-20 top-0 h-[360px] w-[360px] rounded-full bg-[#D8ECFF] blur-3xl" />
                    <div className="absolute -bottom-40 left-1/3 h-[360px] w-[360px] rounded-full bg-[#FFF1C9] blur-3xl" />
                </div>

                <div className="relative grid items-center gap-10 px-7 py-12 sm:px-12 lg:grid-cols-[1.1fr_1fr] lg:py-14">
                    <FadeIn className="order-2 text-center lg:order-1 lg:text-left">
                        <h2 className="font-geistSans text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl sm:leading-[1.05]">
                            {t("bottom_cta_title")}
                        </h2>
                        <p className="mx-auto mb-0 mt-5 max-w-xl text-lg text-slate-600 lg:mx-0">
                            {t("bottom_cta_subtitle")}
                        </p>
                        <div className="mt-9 flex flex-col items-center gap-5 lg:items-start">
                            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-center lg:justify-start">
                                <Button type="primary" onClick={onApply} className="w-full !rounded-full text-base font-semibold sm:w-auto">
                                    <span className="inline-flex items-center gap-2">
                                        {t("bottom_cta_button")}
                                        <SolarIcon name="arrow-right-linear" className="text-lg" />
                                    </span>
                                </Button>
                                <DownloadLink t={t} className="bg-white/70 backdrop-blur" />
                            </div>
                            <p className="mb-0 text-xs text-slate-500">{t("footer_note")}</p>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.05} className="order-1 lg:order-2">
                        <div className="relative mx-auto aspect-[16/9] w-full max-w-[520px] overflow-hidden rounded-3xl shadow-[0_30px_60px_-30px_rgba(76,52,170,0.45)]">
                            <Image
                                src={IMG("module-27.jpg")}
                                alt={t("bottom_cta_image_alt")}
                                fill
                                sizes="(min-width: 1024px) 520px, 100vw"
                                className="object-cover"
                            />
                        </div>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
}

// Le sommaire collant, dans l'ordre de la page.
const SECTION_NAV = ["en-bref", "programme", "projets", "suivi", "certification", "resultats", "faq"];

export default function RocketLanding() {
    const { data: session } = useSession();
    const [showLeadModal, setShowLeadModal] = useState(false);
    const t = useTranslations("RocketAcceleratorLanding");
    const tNav = useTranslations();

    const openModal = () => setShowLeadModal(true);

    return (
        <div className="relative min-h-screen bg-white text-slate-900">
            {!session && (
                <header className="sticky top-0 z-50 border-b border-slate-900/5 bg-[#F8F6FF]/80 backdrop-blur-xl">
                    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
                        <Link href="/" className="flex items-center">
                            <Logo />
                        </Link>
                        <div className="flex items-center gap-6">
                            <a
                                href="#programme"
                                className="hidden text-sm text-slate-500 transition-colors hover:text-slate-900 sm:inline-flex"
                            >
                                {t("nav_programme")}
                            </a>
                            <Link
                                href="/"
                                className="hidden text-sm text-slate-500 transition-colors hover:text-slate-900 sm:inline-flex"
                            >
                                {tNav("back_to_home")}
                            </Link>
                            <button
                                type="button"
                                onClick={openModal}
                                className="cursor-pointer rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                            >
                                {t("cta_primary")}
                            </button>
                        </div>
                    </div>
                </header>
            )}

            <main className="relative">
                <Hero t={t} onApply={openModal} />
                <AcceleratorSectionNav
                    items={SECTION_NAV.map((id) => ({ id, label: t(`section_nav_${id}`) }))}
                    label={t("section_nav_label")}
                    ctaLabel={t("cta_primary")}
                    onCta={openModal}
                    loggedIn={Boolean(session)}
                />
                <Brief t={t} />
                <Creates t={t} />
                <ModernDev t={t} />
                <Programme t={t} onApply={openModal} />
                <Projects t={t} />
                <Rhythm t={t} />
                <Timeline t={t} />
                <FollowUp t={t} />
                <Extras t={t} />
                <Trainer t={t} />
                <Certification t={t} />
                <Stats t={t} />
                <Career t={t} />

                {/* Informations obligatoires avant inscription */}
                <TrainingFactSheet program="rocket" values={trainingFactValues("rocket")} light />

                <Guarantees t={t} />
                <Faq t={t} />
                <FinalCta t={t} onApply={openModal} />
            </main>

            <AcceleratorLeadModal
                open={showLeadModal}
                onOpenChange={setShowLeadModal}
                accelerator="rocket"
                calLink="https://cal.eu/believemy/accelerateur"
            />
        </div>
    );
}
